import {Box} from "@mui/material"
import {CSSProperties, useEffect, useRef, useState, type FC} from "react"
import worldmap from "../../../assets/images/worldmapCaseStudies.png"
import HotSpots from "../../atoms/Hotspots/Hotspots"
import {BannerHeader} from "../../molecules/BannerHeader"
import {CaseStudyCardInfo} from "../../molecules/CaseStudyCardInfo"
import useHomepageTabStore from "../../../stores/homepageTabStore"
import {useTranslation} from "react-i18next"
import {useQuery} from "@tanstack/react-query"
import {useClientSessionStore} from "../../../stores"
import {caseStudiesEndpoints} from "../../../services/api/case-studies"

const hotspots: any[] = [
  {
    id: 1,
    top: "43%",
    left: "24%",
    state: true,
    active: false,
    content: "America",
    caseStudies: [],
  },
  {
    id: 2,
    top: "70%",
    left: "33%",
    state: true,
    active: false,
    content: "South America",
    caseStudies: [],
  },
  {
    id: 3,
    top: "29%",
    left: "51%",
    state: true,
    active: false,
    content: "Europe",
    caseStudies: [],
  },
  {
    id: 4,
    top: "78%",
    left: "88%",
    state: true,
    active: false,
    content: "Australia",
    caseStudies: [],
  },
]

const CaseStudies: FC = () => {
  const [visibleBox, setVisibleBox] = useState(false)
  const [boxContent, setBoxContent] = useState({} as any)
  const [currentPage, setCurrentPage] = useState(0)
  const [boxPosition, setBoxPosition] = useState({
    top: "",
    left: "",
    right: "",
    bottom: "",
  } as CSSProperties)
  const {currentTab, setCurrentTab} = useHomepageTabStore()
  const {t} = useTranslation()
  const sessionClient = useClientSessionStore((state) => state.crmAccount)
  const {getCaseStudies} = caseStudiesEndpoints()
  const boxRef = useRef<HTMLDivElement>(null)
  const {
    data: caseStudies,
    isLoading,
    isFetching,
    isRefetching,
    isError,
    isLoadingError,
    isRefetchError,
  } = useQuery<any[]>({
    queryKey: ["getCaseStudies"],
    queryFn: async () => await getCaseStudies(),
    enabled: !!sessionClient,
  })

  const splitIssueResultSolution = (caseStudies: any[]) => {
    const newObjects: any[] = []

    caseStudies.forEach((item) => {
      const {caseId, title, issue, solution, result} = item

      const issueObject = {
        caseId,
        title,
        type: "issue",
        content: issue,
      }

      const solutionObject = {
        caseId,
        title,
        type: "solution",
        content: solution,
      }

      const resultObject = {
        caseId,
        title,
        type: "result",
        content: result,
      }

      newObjects.push(issueObject, solutionObject, resultObject)
    })

    hotspots.forEach((hotspot, index) => {
      const startIndex = index * 3
      const endIndex = startIndex + 3
      const caseStudiesWithParentId = newObjects
        .slice(startIndex, endIndex)
        .map((caseStudy) => ({
          ...caseStudy,
          parentId: index,
        }))
      hotspot.caseStudies = caseStudiesWithParentId
    })

    const carouselArray: any[] = []
    hotspots.forEach((hotspot, index) => {
      hotspot.caseStudies.forEach((item: any) => {
        carouselArray.push({
          parentId: index,
          ...item,
        })
      })
    })
    return carouselArray
  }

  const carouselArray = splitIssueResultSolution(caseStudies ?? [])
  const handleHotspotClick = (content: {
    index: number
    top: string
    left: string
    state: boolean
    active: boolean
  }) => {
    setToState(content.index)
    setBoxContent(reorderCarouselArray(content.index))
    setCurrentPage(0)
    if (!content.active) {
      setVisibleBox(true)
    }
    let style = {}
    switch (content.index) {
      case 0: {
        style = {
          bottom: "12px",
          right: "12px",
        }
        break
      }
      case 1: {
        style = {
          top: "12px",
          right: "12px",
        }
        break
      }
      case 2: {
        style = {
          bottom: "12px",
          left: "12px",
        }
        break
      }
      case 3: {
        style = {
          top: "12px",
          left: "12px",
        }
        break
      }
      default: {
        style = {
          bottom: "12px",
          left: "12px",
        }
        break
      }
    }

    setBoxPosition(style)
  }

  const reorderCarouselArray = (index: number) => {
    if (!carouselArray) {
      return []
    }
    const newArray = [...carouselArray]
    const elementsToMove = newArray.filter((item) => item.parentId === index)
    const elementsToMoveNext = newArray.filter(
      (item) => item.parentId === (index + 1) % newArray.length,
    )
    const remainingElements = newArray.filter(
      (item) =>
        item.parentId !== index &&
        item.parentId !== (index + 1) % newArray.length,
    )
    return [...elementsToMove, ...elementsToMoveNext, ...remainingElements]
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const settingActive = (content: any) => {
    if (visibleBox) return
    hotspots[content.index].active = content.active
  }

  const setToState = (hotspotIndex: number) => {
    hotspots.forEach((hotspot, index) => {
      if (index === hotspotIndex) {
        hotspot.state = true
        return
      }
      if (!hotspot.active) {
        hotspot.state = false
      } else {
        hotspot.active = false
        hotspot.state = false
      }
    })
  }

  const backdropClose = () => {
    hotspots.forEach((hotspot) => {
      if (hotspot.active) {
        hotspot.active = false
      }
      setVisibleBox(false)
      hotspot.state = true
    })
  }

  useEffect(() => {
    setCurrentTab("Overview")
  }, [])

  useEffect(() => {
    hotspots.forEach((hotspot) => {
      hotspot.active = false
      hotspot.state = true
    })
  }, [currentTab])

  useEffect(() => {
    window.onclick = (event) => {
      if (
        !boxRef.current?.contains(event.target as Node) &&
        event.target !== boxRef.current
      ) {
        backdropClose()
      }
    }
  }, [])

  return (
    <Box
      component={"div"}
      sx={{
        position: "relative",
        borderRadius: "8px",
        zIndex: 0,
      }}
      ref={boxRef}
    >
      <BannerHeader
        style={{
          zIndex: 2,
          top: "10px",
          left: "25px",
        }}
        boxStyle={{
          height: "32px",
          alignContent: "center",
          padding: "0px 8px",
        }}
        titleStyle={{
          color: "#fff",
          fontSize: "16px",
          fontWeight: "bold",
          lineHeight: "24px",
        }}
        themeColor="#226ca6"
        title={t("Explore what we do")}
      />
      <img
        src={worldmap}
        style={{
          maxWidth: "100%",
          backgroundSize: "cover",
          borderRadius: "8px",
          width: "100%",
          height: "100%",
          zIndex: 1,
          position: "relative",
        }}
        onClick={backdropClose}
      />
      {caseStudies &&
      !isLoading &&
      !isFetching &&
      !isRefetching &&
      !isError &&
      !isLoadingError &&
      !isRefetchError ? (
        hotspots.map((hotspot: any, index) => (
          <HotSpots
            key={hotspot.id}
            color="#ba0e68"
            index={index}
            state={hotspot.state}
            active={hotspot.active}
            setActive={settingActive}
            style={{
              top: hotspot.top,
              left: hotspot.left,
              opacity: hotspot.state ? 1 : 0.6,
            }}
            onClick={() => {
              handleHotspotClick({
                index: index,
                top: hotspot.top,
                left: hotspot.left,
                state: hotspot.state,
                active: hotspot.active,
              })
            }}
          />
        ))
      ) : (
        <></>
      )}
      {visibleBox && (
        <CaseStudyCardInfo
          handleHotspotClick={handleHotspotClick}
          boxPosition={boxPosition}
          boxContent={boxContent}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
        />
      )}
    </Box>
  )
}

export default CaseStudies
